import { FoodsIcon, FoodsTags } from "@foodpilot/foods";
import { Box, Button, Collapse, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { CsrIndicator, CsrPillar } from "src/api/client-api/csrStrategy/useGetAllStrategy";
import { IndicatorList } from "./IndicatorList";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { LeverDetailsDrawer } from "./LeverDetailsDrawer";
import { CsrPillarIcon } from "src/components/CsrPillarIcon";
import { Fragment } from "react";

type PillarCardProps = {
    pillar: CsrPillar;
    pillarNumber: number;
};

const DEFAULT_VISIBLE_INDICATORS_COUNT = 3;

export const PillarCard = (props: PillarCardProps) => {
    const { pillar, pillarNumber } = props;
    const theme = useTheme();
    const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
    const { t } = useTranslation();
    const [selectedIndicator, setSelectedIndicator] = useState<CsrIndicator | null>(null);
    const [areOtherIndicatorsVisible, setAreOtherIndicatorsVisible] = useState(false);

    const previewItems = pillar.indicators.slice(0, DEFAULT_VISIBLE_INDICATORS_COUNT);
    const otherItems = pillar.indicators.slice(DEFAULT_VISIBLE_INDICATORS_COUNT);
    const seeMoreIcon = areOtherIndicatorsVisible ? "arrowUpShort" : "arrowDownShort";
    const seeMoreText =
        areOtherIndicatorsVisible ? t("csrstrategy.seelessindicators") : t("csrstrategy.seemoreindicators");
    const otherIndicatorsId = `pillar-${pillar.id}-other-indicators`;

    return (
        <Fragment>
            <Stack flexDirection={"column"} gap={theme.spacing(3)}>
                <Stack flexDirection={"row"} gap={theme.spacing(2)} alignItems={"center"}>
                    <Box
                        sx={{
                            display: "flex",
                            width: theme.spacing(8.75),
                            height: theme.spacing(8.75),
                            flexShrink: 0,
                        }}
                    >
                        <CsrPillarIcon icon={pillar.icon} height={70} backgroundShape="circle" variant="theme" />
                    </Box>
                    <Stack flexDirection={"column"} gap={theme.spacing(0.5)}>
                        <Box width={"fit-content"}>
                            <FoodsTags label={t("csr_strategy.page.pillar", { number: pillarNumber })} />
                        </Box>
                        <Typography variant={"h3"}>{pillar.name}</Typography>
                    </Stack>
                </Stack>
                <Box>
                    <IndicatorList indicators={previewItems} onOpenLeverDetails={setSelectedIndicator} />
                    <Collapse
                        id={otherIndicatorsId}
                        in={areOtherIndicatorsVisible}
                        timeout={prefersReducedMotion ? 0 : "auto"}
                        unmountOnExit
                        sx={{
                            "& .MuiCollapse-wrapperInner": {
                                paddingTop: theme.spacing(3),
                            },
                        }}
                    >
                        <IndicatorList indicators={otherItems} onOpenLeverDetails={setSelectedIndicator} />
                    </Collapse>
                </Box>
                {otherItems.length > 0 && (
                    <Stack flexDirection={"row"} justifyContent={"center"}>
                        <Button
                            variant="text"
                            endIcon={<FoodsIcon size={2} icon={seeMoreIcon} />}
                            aria-controls={otherIndicatorsId}
                            aria-expanded={areOtherIndicatorsVisible}
                            onClick={() => setAreOtherIndicatorsVisible(!areOtherIndicatorsVisible)}
                        >
                            {seeMoreText}
                        </Button>
                    </Stack>
                )}
            </Stack>
            <LeverDetailsDrawer indicator={selectedIndicator} onClose={() => setSelectedIndicator(null)} />
        </Fragment>
    );
};
